import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Sparkles, Loader2, XCircle, Clock, CalendarClock } from "lucide-react";
import { format, parseISO } from "date-fns";
import { nl } from "date-fns/locale";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";

export default function OfferPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [treatmentId, setTreatmentId] = useState("");
  const [time, setTime] = useState("");
  const [showAlt, setShowAlt] = useState(false);
  const [altDate, setAltDate] = useState("");
  const [altTime, setAltTime] = useState("");
  const [altNote, setAltNote] = useState("");

  const specific = data && data.window_start === data.window_end;

  const reload = () => api.get(`/public/offer/${token}`).then((r) => {
    setData(r.data);
    if (r.data.treatments?.length === 1) setTreatmentId(r.data.treatments[0].id);
    if (r.data.window_start) setTime(format(parseISO(r.data.window_start), "HH:mm"));
  }).catch((e) => setError(formatApiError(e.response?.data?.detail) || "Aanbod niet gevonden."));

  useEffect(() => { reload(); }, [token]);

  const claim = async () => {
    const chosenTime = specific ? format(parseISO(data.window_start), "HH:mm") : time;
    if (!treatmentId || !chosenTime) { setError("Kies een behandeling en tijd."); return; }
    setBusy(true); setError("");
    const day = format(parseISO(data.window_start), "yyyy-MM-dd");
    try {
      await api.post(`/public/offer/${token}/claim`, { treatment_id: treatmentId, start: `${day}T${chosenTime}:00` });
      setResult("claimed");
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); reload(); }
    finally { setBusy(false); }
  };
  const decline = async () => {
    setBusy(true);
    try { await api.post(`/public/offer/${token}/decline`); setResult("declined"); }
    catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const submitAlt = async () => {
    setBusy(true); setError("");
    const preferred_start = altDate && altTime ? `${altDate}T${altTime}:00` : null;
    try {
      await api.post(`/public/offer/${token}/alt`, { preferred_start, note: altNote });
      setResult("alt");
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const winTxt = data ? (specific
    ? `${format(parseISO(data.window_start), "EEEE d MMMM 'om' HH:mm", { locale: nl })}`
    : `${format(parseISO(data.window_start), "EEEE d MMMM · HH:mm", { locale: nl })}–${format(parseISO(data.window_end), "HH:mm")}`) : "";

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-4 font-sans">
      <div className="bg-white rounded-3xl border border-[#E8E2D9] shadow-[0_20px_60px_rgb(74,63,63,0.08)] max-w-md w-full p-8">
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-brand text-white flex items-center justify-center mx-auto mb-3 font-display text-lg">NH</div>
          <div className="text-sm text-ink-muted">Nelleke Holt Beauty &amp; Massage</div>
        </div>
        {!data && !error && <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-brand mx-auto" /></div>}
        {result === "claimed" && <Center icon={<CheckCircle2 className="w-12 h-12 text-green-600" />} title="Gelukt, het plekje is voor jou!" text="Ik heb je aanvraag ontvangen en bevestig de afspraak zo snel mogelijk. Tot snel!" />}
        {result === "declined" && <Center icon={<Clock className="w-10 h-10 text-brand" />} title="Bedankt voor je reactie" text="Geen probleem, misschien een volgende keer!" />}
        {result === "alt" && <Center icon={<CalendarClock className="w-10 h-10 text-brand" />} title="Bedankt, ik neem contact op" text="Ik heb je voorkeur ontvangen en kijk of er een moment past dat jou beter uitkomt. Je hoort snel van me!" />}
        {data && !result && (
          <div className="text-center">
            <div className="flex justify-center mb-3"><Sparkles className="w-10 h-10 text-brand" strokeWidth={1.3} /></div>
            <h2 className="text-xl font-display mb-2">Er is een plekje vrij!</h2>
            <p className="text-sm text-ink-muted mb-4">Hallo {data.customer_name}, past dit toevallig in jouw schema?</p>
            <div className="rounded-xl bg-cream-subtle/60 p-4 text-sm text-left mb-4 capitalize">{winTxt}</div>
            {data.comment && <div className="rounded-xl bg-[#FBF5EC] border-l-[3px] border-brand text-left p-3 text-sm mb-4">{data.comment}</div>}
            {(data.status !== "open" || data.expired) ? (
              <div className="rounded-xl bg-[#FBEBD2] border border-dashed border-[#E0A24A] text-[#9A5B12] p-4 text-sm" data-testid="offer-unavailable">
                {data.expired ? "De reactietijd voor dit aanbod is helaas verstreken." : "Dit plekje is helaas al vergeven."}
              </div>
            ) : showAlt ? (
              <div className="text-left" data-testid="offer-alt-form">
                <p className="text-sm text-ink-muted mb-3">Geef je voorkeur door, dan kijk ik of er een ander moment past dat jou beter uitkomt.</p>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div>
                    <label className="text-sm text-ink-muted">Datum (optioneel)</label>
                    <input type="date" value={altDate} onChange={(e) => setAltDate(e.target.value)} data-testid="offer-alt-date"
                      className="mt-1 w-full rounded-xl border border-[#E8E2D9] p-2.5 text-sm bg-white" />
                  </div>
                  <div>
                    <label className="text-sm text-ink-muted">Tijd (optioneel)</label>
                    <input type="time" step="900" value={altTime} onChange={(e) => setAltTime(e.target.value)} data-testid="offer-alt-time"
                      className="mt-1 w-full rounded-xl border border-[#E8E2D9] p-2.5 text-sm bg-white" />
                  </div>
                </div>
                <label className="text-sm text-ink-muted">Toelichting (optioneel)</label>
                <textarea value={altNote} onChange={(e) => setAltNote(e.target.value)} rows={3} data-testid="offer-alt-note"
                  placeholder="Bijv. liever in het weekend of 's avonds..." className="mt-1 w-full rounded-xl border border-[#E8E2D9] p-2.5 text-sm mb-3" />
                {error && <p className="text-sm text-destructive mb-3">{error}</p>}
                <div className="space-y-2">
                  <Button onClick={submitAlt} disabled={busy} data-testid="offer-alt-submit-btn" className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-12">Verstuur mijn voorkeur</Button>
                  <Button onClick={() => { setShowAlt(false); setError(""); }} variant="outline" data-testid="offer-alt-back-btn" className="w-full rounded-full h-11 border-[#E8E2D9]">Terug</Button>
                </div>
              </div>
            ) : (
              <>
                {data.treatments.length > 1 && (
                  <div className="text-left mb-3">
                    <label className="text-sm text-ink-muted">Behandeling</label>
                    <select value={treatmentId} onChange={(e) => setTreatmentId(e.target.value)} data-testid="offer-treatment"
                      className="mt-1 w-full rounded-xl border border-[#E8E2D9] p-2.5 text-sm bg-white">
                      <option value="">Kies...</option>
                      {data.treatments.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.duration_min} min · € {Number(t.price).toFixed(2)}</option>)}
                    </select>
                  </div>
                )}
                {specific ? (
                  <div className="text-left mb-4">
                    <label className="text-sm text-ink-muted">Tijd</label>
                    <div className="mt-1 w-full rounded-xl border border-[#E8E2D9] bg-cream-subtle/40 p-2.5 text-sm" data-testid="offer-fixed-time">
                      {format(parseISO(data.window_start), "HH:mm")} uur
                    </div>
                  </div>
                ) : (
                  <div className="text-left mb-4">
                    <label className="text-sm text-ink-muted">Hoe laat wil je komen?</label>
                    <input type="time" step="900" value={time} onChange={(e) => setTime(e.target.value)} data-testid="offer-time"
                      min={format(parseISO(data.window_start), "HH:mm")} max={format(parseISO(data.window_end), "HH:mm")}
                      className="mt-1 w-full rounded-xl border border-[#E8E2D9] p-2.5 text-sm" />
                  </div>
                )}
                {error && <p className="text-sm text-destructive mb-3">{error}</p>}
                <div className="space-y-2">
                  <Button onClick={claim} disabled={busy} data-testid="offer-claim-btn" className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-12">Ja, ik neem dit plekje</Button>
                  <Button onClick={() => { setShowAlt(true); setError(""); }} disabled={busy} variant="outline" data-testid="offer-alt-btn" className="w-full rounded-full h-11 border-[#E8E2D9]">Ja, maar liever een ander moment</Button>
                  <Button onClick={decline} disabled={busy} variant="ghost" data-testid="offer-decline-btn" className="w-full rounded-full h-11 text-ink-muted">Nee, past niet</Button>
                </div>
                {data.first_come && <p className="text-xs text-ink-muted mt-3">Wie het eerst reageert, heeft de plek.</p>}
              </>
            )}          </div>
        )}
        {error && !data && <Center icon={<XCircle className="w-10 h-10 text-brand" />} title="Oeps" text={error} />}
      </div>
    </div>
  );
}

function Center({ icon, title, text }) {
  return (
    <div className="text-center py-4">
      <div className="flex justify-center mb-4">{icon}</div>
      <h2 className="text-xl font-display mb-2">{title}</h2>
      <p className="text-sm text-ink-muted leading-relaxed">{text}</p>
    </div>
  );
}
